// DS EDIT 8 — suggested-prompts visibility, account-synced (was localStorage-only).
import { useCallback, useEffect, useRef, useState } from "react";
import { useChatPreferences } from "@/hooks/useChatPreferences";

const LEGACY_KEY = "deeply.chat.suggestedPrompts.visible";

function readLegacy(): boolean | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = localStorage.getItem(LEGACY_KEY);
    if (raw === "true") return true;
    if (raw === "false") return false;
    return null;
  } catch {
    return null;
  }
}

/**
 * Same contract as the old usePersistentToggle("chat.suggestedPrompts.visible"):
 * legacy localStorage seeds first render; an explicit server choice wins after
 * hydration (legacy key then retired); changes write through the synced store.
 */
export function useSuggestedPromptsVisibility(defaultValue = true) {
  const { preferences, isHydrated, updatePreference } = useChatPreferences();
  const serverValue = preferences.uiPreferences.showSuggestedPrompts;

  const [value, setValueState] = useState<boolean>(() => readLegacy() ?? defaultValue);

  const reconciledRef = useRef(false);
  useEffect(() => {
    if (!isHydrated || reconciledRef.current) return;
    reconciledRef.current = true;
    if (serverValue !== undefined) {
      setValueState(serverValue);
      try {
        localStorage.removeItem(LEGACY_KEY); // server copy confirmed
      } catch {}
      return;
    }
    const legacy = readLegacy();
    if (legacy !== null) {
      updatePreference("uiPreferences", {
        ...preferences.uiPreferences,
        showSuggestedPrompts: legacy,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot reconcile on hydration
  }, [isHydrated]);

  const setValue = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      setValueState((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        try {
          if (typeof window !== "undefined") {
            localStorage.setItem(LEGACY_KEY, resolved ? "true" : "false");
          }
        } catch {}
        updatePreference("uiPreferences", {
          ...preferences.uiPreferences,
          showSuggestedPrompts: resolved,
        });
        return resolved;
      });
    },
    [updatePreference, preferences.uiPreferences]
  );

  return { value, setValue };
}
